import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { Terminal, Play, Loader } from 'lucide-react'
import api from '../api'

const QUICK_PORTS = [
  { port: 21,    label: 'FTP',        color: '#60a5fa' },
  { port: 22,    label: 'SSH',        color: '#34d399' },
  { port: 23,    label: 'Telnet',     color: '#fbbf24' },
  { port: 25,    label: 'SMTP',       color: '#60a5fa' },
  { port: 80,    label: 'HTTP',       color: '#34d399' },
  { port: 110,   label: 'POP3',       color: '#60a5fa' },
  { port: 143,   label: 'IMAP',       color: '#60a5fa' },
  { port: 443,   label: 'HTTPS',      color: '#34d399' },
  { port: 3306,  label: 'MySQL',      color: '#f87171' },
  { port: 5432,  label: 'PostgreSQL', color: '#f87171' },
  { port: 6379,  label: 'Redis',      color: '#f87171' },
  { port: 27017, label: 'MongoDB',    color: '#f87171' },
]

export default function TelnetTool({ wanIp }) {
  const [host, setHost]       = useState('')
  const [port, setPort]       = useState('80')
  const [command, setCommand] = useState('')
  const [timeoutMs, setTimeoutMs] = useState(5000)
  const [result, setResult]   = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => { if (wanIp && !host) setHost(wanIp) }, [wanIp])

  const connect = async () => {
    const h = host.trim()
    const p = parseInt(port)
    if (!h) return toast.error('Vui lòng nhập Host/IP')
    if (!p || p < 1 || p > 65535) return toast.error('Port không hợp lệ (1–65535)')
    setLoading(true)
    setResult(null)
    try {
      const res = await api.post('/telnet', {
        host: h, port: p,
        command: command.trim() || undefined,
        timeout: timeoutMs,
      })
      setResult(res.data)
      if (res.data.connected) toast.success('Kết nối thành công!')
      else toast.error('Không thể kết nối: ' + (res.data.error || res.data.status))
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Lỗi không xác định'
      toast.error('Lỗi: ' + msg)
      setResult({ connected: false, status: 'error', error: msg })
    } finally { setLoading(false) }
  }

  const statusStyle = result
    ? result.connected
      ? { color: '#34d399', icon: '✅', label: 'Kết nối thành công' }
      : result.status === 'timeout'
        ? { color: '#fbbf24', icon: '⏱', label: 'Timeout' }
        : { color: '#f87171', icon: '❌', label: 'Không thể kết nối' }
    : null

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
          background:'linear-gradient(135deg,#a855f7,#7e22ce)',
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Terminal size={20} color="#fff" />
        </div>
        <div>
          <h1 style={{ fontSize:18, fontWeight:700 }}>Telnet / TCP Connect</h1>
          <p style={{ fontSize:13, color:'var(--text3)' }}>Kết nối TCP thô, gửi lệnh và đọc phản hồi từ server</p>
        </div>
      </div>

      {/* Form */}
      <div className="grad-border p-5 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Host / IP</label>
            <input className="inp" placeholder="192.168.1.1 hoặc mail.example.com"
              value={host} onChange={e => setHost(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && connect()} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Port</label>
            <input className="inp" type="number" min="1" max="65535"
              value={port} onChange={e => setPort(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && connect()} />
          </div>
        </div>

        <div>
          <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>
            Lệnh gửi <span style={{ color:'var(--text3)', fontWeight:400 }}>(tùy chọn – gửi ngay sau khi kết nối)</span>
          </label>
          <input className="inp font-mono" style={{ fontSize:13 }}
            placeholder="GET / HTTP/1.0   hoặc   EHLO test   hoặc   USER anonymous"
            value={command} onChange={e => setCommand(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && connect()} />
        </div>

        <div style={{ display:'flex', alignItems:'flex-end', gap:16 }}>
          <div>
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Timeout</label>
            <select className="inp" style={{ width:140 }} value={timeoutMs}
              onChange={e => setTimeoutMs(parseInt(e.target.value))}>
              <option value={3000}>3 giây</option>
              <option value={5000}>5 giây</option>
              <option value={8000}>8 giây</option>
              <option value={10000}>10 giây</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={connect} disabled={loading}>
            {loading
              ? <><Loader size={14} className="animate-spin" /> Đang kết nối...</>
              : <><Play size={14} /> Kết nối</>}
          </button>
        </div>

        {/* Result */}
        {result && statusStyle && (
          <div className="fade-in" style={{ borderTop:'1px solid var(--border)', paddingTop:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
              <span style={{ fontSize:15, fontWeight:700, color: statusStyle.color }}>
                {statusStyle.icon} {statusStyle.label}
              </span>
              {result.latency !== undefined && (
                <span className="chip chip-blue" style={{ fontFamily:'JetBrains Mono,monospace' }}>
                  {result.latency}ms
                </span>
              )}
            </div>

            {/* banner = dữ liệu trả về từ server */}
            {result.banner && (
              <div>
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:6 }}>📥 Phản hồi từ server:</div>
                <pre className="log-output">{result.banner}</pre>
              </div>
            )}

            {!result.banner && result.connected && (
              <div style={{ fontSize:13, color:'var(--text2)', padding:'10px 14px',
                borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)' }}>
                Kết nối thành công nhưng server không gửi dữ liệu. Có thể cần gửi lệnh cụ thể.
              </div>
            )}

            {result.error && (
              <div className="log-output log-error" style={{ fontSize:13 }}>{result.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Quick ports */}
      <div className="grad-border p-5">
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:12 }}>⚡ Chọn nhanh port phổ biến</h3>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {QUICK_PORTS.map(p => (
            <button key={p.port} onClick={() => setPort(String(p.port))}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
                borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)',
                cursor:'pointer', transition:'all .18s', width:'100%' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.background = p.color + '10'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg)'; }}
            >
              <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:12, fontWeight:700,
                color: p.color, background: p.color+'18', border:`1px solid ${p.color}40`,
                padding:'2px 7px', borderRadius:5, flexShrink:0 }}>
                {p.port}
              </span>
              <span style={{ fontSize:13, color:'var(--text)', fontWeight:500 }}>{p.label}</span>
            </button>
          ))}
        </div>

        <div style={{ marginTop:16, padding:'12px 14px', borderRadius:8,
          background:'var(--bg)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:12, color:'var(--text3)', marginBottom:8 }}>💡 Ví dụ lệnh:</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4, fontFamily:'JetBrains Mono,monospace', fontSize:13 }}>
            {[
              { label:'HTTP',   cmd:'GET / HTTP/1.0',   port:80  },
              { label:'SMTP',   cmd:'EHLO test',         port:25  },
              { label:'POP3',   cmd:'USER test',         port:110 },
              { label:'FTP',    cmd:'USER anonymous',    port:21  },
            ].map(ex => (
              <div key={ex.label} style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span style={{ color:'var(--accent)', minWidth:55 }}>{ex.label}:</span>
                <span style={{ color:'var(--text2)', flex:1 }}>{ex.cmd}</span>
                <button onClick={() => { setPort(String(ex.port)); setCommand(ex.cmd); }}
                  style={{ fontSize:11, color:'var(--accent)', background:'none', border:'1px solid var(--border)',
                    borderRadius:4, padding:'2px 8px', cursor:'pointer' }}>
                  Dùng
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
